/**
 * Employee Compliance Center — replaces the legacy Staff panel.
 *
 * Two views:
 *   1. Directory  — dashboard cards + searchable employee list
 *   2. Profile    — full compliance dashboard for the selected employee
 *
 * Employees whose compliance_status is anything other than `ready_for_dispatch`
 * are automatically hidden from the Dispatch Center's staff picker (server-side
 * enforcement in /api/staff/availability + /api/staff-directory).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Search, Plus, ArrowLeft, RefreshCw, Users, ShieldCheck, ClipboardList,
  Clock3, CalendarX2, Gauge, Building2, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  listEmployees, getEmployeesDashboard, migrateEmployees,
  createEmployee,
  EMPLOYEE_COMPLIANCE_STATUSES,
} from "@/lib/api";
import EmployeeProfile from "@/components/employees/EmployeeProfile";
import EmployeePhoto from "@/components/employees/EmployeePhoto";
import AddEmployeeDialog from "@/components/employees/AddEmployeeDialog";

const STATUS_META = Object.fromEntries(EMPLOYEE_COMPLIANCE_STATUSES.map((s) => [s.value, s]));

const STATUS_FILTERS = [
  { id: "all",                 label: "All" },
  { id: "ready_for_dispatch",  label: "Ready for Dispatch" },
  { id: "onboarding",          label: "Onboarding" },
  { id: "new_hire",            label: "New Hire" },
  { id: "compliance_review",   label: "Compliance Review" },
  { id: "suspended",           label: "Suspended" },
];

export default function EmployeeComplianceCenter({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, db] = await Promise.all([listEmployees(), getEmployeesDashboard()]);
      setEmployees(list.employees || []);
      setDashboard(db);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load employees.");
    } finally { setLoading(false); }
  }, [onSessionExpired]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = employees;
    if (filter !== "all") out = out.filter((e) => e.compliance_status === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((e) =>
        [e.full_name, e.employee_id, e.position, e.email, e.phone, e.department]
          .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return out;
  }, [employees, query, filter]);

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await migrateEmployees();
      toast.success(
        res.created_count
          ? `Migrated ${res.created_count} employee${res.created_count === 1 ? "" : "s"}; ${res.documents_linked} documents linked.`
          : `No new employees to migrate. ${res.documents_linked} documents linked.`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed.");
    } finally { setMigrating(false); }
  };

  const handleCreate = async (form) => {
    try {
      const emp = await createEmployee(form);
      toast.success(`Created ${emp.employee_id} · ${emp.full_name}`);
      setShowAdd(false);
      await load();
      setActiveId(emp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create employee.");
    }
  };

  // ---------- PROFILE VIEW ----------
  if (activeId) {
    return (
      <EmployeeProfile
        employeeId={activeId}
        onBack={() => { setActiveId(null); load(); }}
        onSessionExpired={onSessionExpired}
      />
    );
  }

  // ---------- DIRECTORY VIEW ----------
  return (
    <div className="space-y-6" data-testid="employee-compliance-center">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
            Employee Compliance Center
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
            Onboarding progress, credentials, and dispatch readiness for every team member.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleMigrate} disabled={migrating}
                  className="h-10" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="employees-migrate-button">
            {migrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync from Docs
          </Button>
          <Button onClick={() => setShowAdd(true)} className="h-10"
                  style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="employees-add-button">
            <Plus className="h-4 w-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Dashboard cards */}
      <DashboardCards data={dashboard} />

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input className="pl-9 h-10" placeholder="Search name, ID, position, email…"
                 value={query} onChange={(e) => setQuery(e.target.value)}
                 data-testid="employees-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button key={s.id} onClick={() => setFilter(s.id)}
                    data-testid={`employees-filter-${s.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                    style={{
                      background: filter === s.id ? "#0B2545" : "#FFFFFF",
                      color: filter === s.id ? "#FFD774" : "#475569",
                      borderColor: filter === s.id ? "#0B2545" : "#E2E8F0",
                    }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Directory list */}
      {loading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasData={employees.length > 0} onAdd={() => setShowAdd(true)} onMigrate={handleMigrate} migrating={migrating} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="employees-grid">
          {filtered.map((e) => (
            <EmployeeCard key={e.id} employee={e} onOpen={() => setActiveId(e.id)} />
          ))}
        </div>
      )}

      <AddEmployeeDialog open={showAdd} onClose={() => setShowAdd(false)} onCreate={handleCreate} />
    </div>
  );
}

// =========================================================================
// Dashboard cards
// =========================================================================
const DashboardCards = ({ data }) => {
  const cards = [
    { label: "Total Employees",          value: data?.total_employees ?? "—",         icon: Users,          accent: "#0B2545" },
    { label: "Ready for Dispatch",       value: data?.ready_for_dispatch ?? "—",      icon: ShieldCheck,    accent: "#0F6D5D" },
    { label: "Currently Onboarding",     value: data?.currently_onboarding ?? "—",    icon: ClipboardList,  accent: "#7A5E12" },
    { label: "Pending Verification",     value: data?.pending_verification ?? "—",    icon: Clock3,         accent: "#B45309" },
    { label: "Expiring in 30 Days",      value: data?.expiring_within_30_days ?? "—", icon: CalendarX2,     accent: "#9B1C1C" },
    { label: "Compliance Score",         value: data ? `${data.compliance_score}%` : "—", icon: Gauge,      accent: "#1D4ED8" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="employees-dashboard-cards">
      {cards.map((c) => (
        <div key={c.label} className="rounded-2xl border bg-white p-4"
             style={{ borderColor: "#E2E8F0" }}
             data-testid={`dashboard-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between">
            <span className="grid place-items-center h-9 w-9 rounded-lg"
                  style={{ background: `${c.accent}14`, color: c.accent }}>
              <c.icon className="h-4 w-4" />
            </span>
            <span className="h-1.5 w-8 rounded-full" style={{ background: c.accent }} />
          </div>
          <div className="mt-3 font-display text-2xl font-bold" style={{ color: "#0B2545" }}>{c.value}</div>
          <div className="text-[11px] uppercase tracking-[0.16em] mt-1" style={{ color: "#64748B" }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
};

// =========================================================================
// Employee card
// =========================================================================
const EmployeeCard = ({ employee, onOpen }) => {
  const status = STATUS_META[employee.compliance_status] || STATUS_META.new_hire;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-2xl border bg-white p-4 hover:shadow-md transition"
      style={{ borderColor: "#E2E8F0" }}
      data-testid={`employee-card-${employee.id}`}
    >
      <div className="flex items-start gap-3">
        <EmployeePhoto employee={employee} size={56} rounded="rounded-full" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-semibold truncate" style={{ color: "#0B2545" }}>
            {employee.full_name}
          </div>
          <div className="text-xs truncate" style={{ color: "#64748B" }}>
            {employee.position || "—"}{employee.department ? ` · ${employee.department}` : ""}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "#94A3B8" }}>
            {employee.employee_id}{employee.hire_date ? ` · Hired ${_fmtDate(employee.hire_date)}` : ""}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full"
              style={{ background: status.bg, color: status.color }}>
          {status.label}
        </span>
        <div className="flex items-center gap-2 text-[11px]" style={{ color: "#475569" }}>
          {employee.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /></span>}
          {employee.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /></span>}
          {employee.service_area && (
            <span className="inline-flex items-center gap-1 truncate max-w-[140px]" title={employee.service_area}>
              <Building2 className="h-3 w-3" />{employee.service_area}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// =========================================================================
// Empty state
// =========================================================================
const EmptyState = ({ hasData, onAdd, onMigrate, migrating }) => (
  <div className="rounded-2xl border-2 border-dashed py-16 text-center"
       style={{ borderColor: "#E2E8F0", color: "#64748B" }}
       data-testid="employees-empty">
    <Users className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
    <p className="font-medium" style={{ color: "#0B2545" }}>
      {hasData ? "No employees match the current filters." : "No employees yet"}
    </p>
    {!hasData && (
      <>
        <p className="text-sm mt-1 max-w-md mx-auto">
          Add your first employee, or sync existing team members from uploaded Operations &amp; Docs records.
        </p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button onClick={onAdd} style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="employees-empty-add">
            <Plus className="h-4 w-4 mr-2" /> Add Employee
          </Button>
          <Button variant="outline" onClick={onMigrate} disabled={migrating}
                  style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="employees-empty-migrate">
            {migrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync from Docs
          </Button>
        </div>
      </>
    )}
  </div>
);

// ---- helpers ----
const _fmtDate = (v) => { try { return format(parseISO(v), "PP"); } catch { return v; } };
